import { parseRemixDataConsumerSource } from '@atlassian/conversation-assistant-action-helpers/utils/confluence-actions';
import { getMauiIdFromAdfNode } from '@atlassian/malleable-ui-components/editor-remix/remix-ids';

import type { CwrRemixResult } from '../types/cwr-remix-result';

type AdfNode = {
	attrs?: Record<string, unknown>;
	content?: unknown;
	marks?: Array<{ attrs?: { sources?: unknown }; type?: string }>;
	type?: string;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
	typeof value === 'object' && value !== null;

const getRemixSource = (node: AdfNode): string | undefined => {
	const sources = node.marks?.find((mark) => mark.type === 'dataConsumer')?.attrs?.sources;
	return Array.isArray(sources) && typeof sources[0] === 'string' ? sources[0] : undefined;
};

const getUserAction = (operation: string): CwrRemixResult['userAction'] =>
	operation === 'replaceNode' ? 'edit' : 'insert';

const collectCwrVisualCandidates = (
	node: unknown,
	operation: string,
	results: CwrRemixResult[],
): void => {
	if (Array.isArray(node)) {
		node.forEach((child) => collectCwrVisualCandidates(child, operation, results));
		return;
	}
	if (!isRecord(node)) {
		return;
	}
	const adfNode = node as AdfNode;

	const mauiId = getMauiIdFromAdfNode(adfNode);
	const localId = adfNode.attrs?.localId;
	if (mauiId && typeof localId === 'string') {
		results.push({
			createdContentMetadata: { remixId: mauiId },
			createdContentType: 'maui-app',
			identity: { attribute: 'mauiVersion', localId, value: mauiId },
			userAction: getUserAction(operation),
		});
	}

	if (adfNode.type === 'media') {
		const mediaFileId = adfNode.attrs?.id;
		const remixSource = getRemixSource(adfNode);
		const remixMetadata = parseRemixDataConsumerSource(remixSource);
		if (typeof mediaFileId === 'string') {
			results.push({
				createdContentMetadata: { mediaFileId },
				createdContentType: remixMetadata?.type ?? 'image',
				...(remixMetadata && { dynamicUiSubtype: remixMetadata.subtype }),
				identity: { attribute: 'id', value: mediaFileId },
				userAction: getUserAction(operation),
			});
		}
	}

	collectCwrVisualCandidates(adfNode.content, operation, results);
};

/**
 * Extracts visual candidates from completed CWR update ADF. Callers compare identities
 * with the pre-operation document so carried-forward visuals are not registered as new results.
 */
export const extractCwrRemixResults = (operation: string, value: string): CwrRemixResult[] => {
	try {
		const results: CwrRemixResult[] = [];
		collectCwrVisualCandidates(JSON.parse(value), operation, results);
		return results;
	} catch {
		return [];
	}
};
